/**
 * Direct-mesh attach helpers — the ONE place a per-entity direct mesh (arrow
 * decorator, hatch / wipeout fill) is registered on its `EntityRender` and
 * given its initial visibility. Extracted from `sceneBulkLoadHelpers.ts`
 * (file-size cap) when the block-reference layer chain landed (Codex PR #784
 * round 3 P1): every attach site records BOTH layer fields the composite
 * visibility reads (`recordEntityRenderLayers`) and applies the composite
 * (`applyEntityRenderVisibility`) — layer chain AND a `lodHidden` flag the
 * per-frame cull may already have pre-seeded — instead of a bare
 * `hiddenLayers.has(layer)` write.
 *
 * `upsertEntityRender` lives here (its natural home — every attach upserts)
 * and is re-exported from `sceneBulkLoadHelpers.ts` so the existing import
 * paths keep resolving (ПРИОРИТЕТ #2 dep-graph seam).
 */

import type * as THREE from "three";

import type { RenderableEntity } from "../types";
import type { EntityRender } from "./SceneRefsTypes";
import { applyEntityRenderVisibility } from "./entityRenderVisibility";

/**
 * Read-or-create the EntityRender entry for `entityId`. Opt 8 — keeps
 * per-entity field mutations as a single Map round-trip whether the
 * entry already exists or not.
 */
export function upsertEntityRender(
  entityRenders: Map<string, EntityRender>,
  entityId: string,
): EntityRender {
  let render = entityRenders.get(entityId);
  if (!render) {
    render = {};
    entityRenders.set(entityId, render);
  }
  return render;
}

/**
 * Attach a set of arrow decorator meshes (dimension arrow / tick heads or
 * multileader leader arrows) directly to the scene and record them on the
 * source entity's `EntityRender` so `sceneTeardown` disposes them and
 * `applyAndMirrorLayerVisibility` toggles them by layer.
 *
 * PR-F2 — the hidden-layer visibility is applied immediately at emit
 * (belt-and-suspenders to `applyAndMirrorLayerVisibility`); without this
 * initial set the first frame between emit and the layer-visibility pass
 * would briefly paint arrow orphans on hidden layers (codex P2 on PR #63).
 * No-op when `arrowMeshes` is empty.
 */
export function attachArrowDecoratorMeshes(
  scene: THREE.Scene,
  renderable: RenderableEntity,
  arrowMeshes: Array<THREE.InstancedMesh | THREE.LineSegments>,
  entityRenders: Map<string, EntityRender>,
  hiddenLayers?: ReadonlySet<string>,
): void {
  if (arrowMeshes.length === 0) return;
  for (const am of arrowMeshes) scene.add(am);
  const render = upsertEntityRender(entityRenders, renderable.id);
  render.arrowDecoratorMeshes = arrowMeshes;
  // Codex PR #784 round-10 P1 — the relift emitter's SYNC-channel idempotence
  // marker, so a cull → relift of a bulk-loaded entity does not stack a second
  // set of decorators (see `EntityRender.directMeshesEmitted`).
  render.directMeshesEmitted = true;
  recordEntityRenderLayers(render, renderable);
  if (hiddenLayers) applyEntityRenderVisibility(render, hiddenLayers);
}

/**
 * Record the layer fields the composite visibility reads off an
 * `EntityRender` — the renderable's OWN layer and its block-reference layer
 * chain (Codex PR #784 round 3 P1: a block child hides with its enclosing
 * INSERT's layer too). Every direct-mesh attach site (arrows, fills, text)
 * routes through here so the two fields can never drift apart.
 */
export function recordEntityRenderLayers(
  render: EntityRender,
  renderable: RenderableEntity,
): void {
  render.entityLayer = renderable.layer;
  if (renderable.renderParentLayers) {
    render.parentLayers = renderable.renderParentLayers;
  }
}

/**
 * Attach a hatch / wipeout fill mesh to the scene under the renderable's
 * `EntityRender.fillMesh`, applying the CURRENT hidden-layer state at emit
 * (FIX Codex S9b P2 — register the fill's layer so `LayerVisibilityEffect`
 * toggles it, and an MLEADER block-content HATCH child, re-homed onto the
 * parent layer, hides with the callout; mirrors the arrow-decorator emit
 * path). The composite (`applyEntityRenderVisibility`) honours the
 * block-reference chain and a `lodHidden` flag the cull may have pre-seeded.
 */
export function attachFillMesh(
  scene: THREE.Scene,
  renderable: RenderableEntity,
  fillMesh: THREE.Mesh,
  renderOrder: number,
  entityRenders: Map<string, EntityRender>,
  hiddenLayers?: ReadonlySet<string>,
): void {
  fillMesh.renderOrder = renderOrder;
  fillMesh.frustumCulled = false;
  scene.add(fillMesh);
  const render = upsertEntityRender(entityRenders, renderable.id);
  render.fillMesh = fillMesh;
  // Codex PR #784 round-10 P1 — SYNC-channel idempotence marker (see
  // `attachArrowDecoratorMeshes` / `EntityRender.directMeshesEmitted`).
  render.directMeshesEmitted = true;
  recordEntityRenderLayers(render, renderable);
  if (hiddenLayers) applyEntityRenderVisibility(render, hiddenLayers);
}
